import { Control, UseFormWatch } from 'react-hook-form';
import {
  ComboboxField,
  CustomTextInputField,
  DatePickerField,
  SelectField,
  TextInputField,
} from '@brighte/form-components';
import { AU_STATES_CODE, IdentificationType, IdentityType, isBusinessLoanProduct } from '@brighte/validation';
import { useApplicationData } from '@provider';
import { Columns } from '@spark-web/columns';
import { IDENTIFICATION, LABELS, PLACEHOLDER } from '@utils';
import { getCountryCode, getExpiryDateFieldName, getIdentificationOptions } from './helpers';

type Props = {
  control: Control<IdentityType>;
  watch: UseFormWatch<IdentityType>;
  name: string;
  isExistingUser?: boolean;
};

function IdentificationForm({ control, watch, name, isExistingUser = false }: Props) {
  const {
    IDENTIFICATION_TYPE,
    FIRST_NAME,
    MIDDLE_NAME,
    LAST_NAME,
    MEDICARE_NUMBER,
    MEDICARE_REFERENCE,
    PASSPORT_NUMBER,
    ISSUING_COUNTRY,
    LICENCE_NUMBER,
    LICENCE_CARD_NUMBER,
    EXPIRY_DATE,
    DATE_OF_BIRTH,
    PHOTO_ID_NUMBER,
    ISSUING_COUNTRY_PLACEHOLDER,
    EMAIL_ADDRESS,
    PHONE_NUMBER,
    DRIVER_LICENCE_NUMBER,
  } = LABELS;
  const {
    MEDICARE_CARD_COLOUR_OPTIONS,
    MEDICARE_CARD_DESCRIPTION,
    MEDICARE_REF_DESCRIPTION,
    IDENTIFICATION_LICENCE_DESCRIPTION,
    DEFAULT_LICENCE_NUMBER_DESCRIPTION,
    DEFAULT_LICENCE_CARD_NUMBER_DESCRIPTION,
    ISSUE_STATE,
  } = IDENTIFICATION;

  const { quote } = useApplicationData(['quote']);
  const financialProductId = quote?.financialProduct?.id ?? '';
  const identificationType = watch(`${name}.identification.type`);
  const stateCode = watch(`${name}.identification.drivingLicense.state`);

  const expiryDateField = getExpiryDateFieldName(identificationType, name);
  const dob = watch(`${name}.dateOfBirth`);
  // dob field is optional for user is existing user. We don't want to disable empty dob field
  const isExistingUserDob = dob && isExistingUser;
  const isSme = isBusinessLoanProduct(financialProductId);

  return (
    <>
      {!isSme ? (
        <SelectField
          name={`${name}.identification.type`}
          control={control}
          label={IDENTIFICATION_TYPE}
          options={getIdentificationOptions(financialProductId)}
          placeholder={PLACEHOLDER}
        />
      ) : null}
      {identificationType ? (
        <>
          {identificationType === IdentificationType.MEDICARE ? (
            <SelectField
              name={`${name}.identification.medicare.color`}
              control={control}
              label=""
              options={MEDICARE_CARD_COLOUR_OPTIONS}
              placeholder="Card colour"
            />
          ) : null}
          {identificationType === IdentificationType.DRIVING_LICENSE && !isSme ? (
            <SelectField
              name={`${name}.identification.drivingLicense.state`}
              control={control}
              label=""
              options={AU_STATES_CODE}
              placeholder="State or Territory"
            />
          ) : null}
          {identificationType === IdentificationType.PHOTO_ID ? (
            <>
              <TextInputField
                name={`${name}.identification.photoId.number`}
                control={control}
                label={PHOTO_ID_NUMBER}
                autoComplete="off"
              />
              <SelectField
                name={`${name}.identification.photoId.state`}
                control={control}
                label=""
                options={AU_STATES_CODE}
                placeholder="State or Territory"
              />
            </>
          ) : null}
          <TextInputField
            name={`${name}.firstName`}
            control={control}
            label={FIRST_NAME}
            FieldProps={{
              disabled: isExistingUser,
            }}
          />
          <TextInputField
            name={`${name}.middleName`}
            control={control}
            label={MIDDLE_NAME}
            FieldProps={{ secondaryLabel: '(Optional)', disabled: isExistingUser }}
          />
          <TextInputField
            name={`${name}.lastName`}
            control={control}
            label={LAST_NAME}
            FieldProps={{
              disabled: isExistingUser,
            }}
          />
          {isSme ? (
            <>
              <TextInputField name={`${name}.email`} control={control} label={EMAIL_ADDRESS} />
              <TextInputField name={`${name}.phone`} control={control} label={PHONE_NUMBER} />
            </>
          ) : null}

          {identificationType === IdentificationType.MEDICARE ? (
            <>
              <TextInputField
                name={`${name}.identification.medicare.number`}
                control={control}
                label={MEDICARE_NUMBER}
                FieldProps={{ description: MEDICARE_CARD_DESCRIPTION }}
                autoComplete="off"
              />
              <TextInputField
                name={`${name}.identification.medicare.reference`}
                control={control}
                label={MEDICARE_REFERENCE}
                FieldProps={{ description: MEDICARE_REF_DESCRIPTION }}
              />
            </>
          ) : null}
          {identificationType === IdentificationType.PASSPORT ? (
            <>
              <TextInputField
                name={`${name}.identification.passport.number`}
                control={control}
                label={PASSPORT_NUMBER}
                autoComplete="off"
              />
              <ComboboxField
                name={`${name}.identification.passport.issuingCountry`}
                control={control}
                label={ISSUING_COUNTRY}
                items={getCountryCode()}
                placeholder={ISSUING_COUNTRY_PLACEHOLDER}
                getOptionValue={option => option.label}
              />
            </>
          ) : null}
          {identificationType === IdentificationType.DRIVING_LICENSE && (isSme || stateCode) ? (
            <>
              <TextInputField
                name={`${name}.identification.drivingLicense.number`}
                control={control}
                label={isSme ? DRIVER_LICENCE_NUMBER : LICENCE_NUMBER}
                FieldProps={{
                  description: stateCode
                    ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode].LICENCE_NUMBER_DESCRIPTION
                    : DEFAULT_LICENCE_NUMBER_DESCRIPTION,
                }}
                autoComplete="off"
              />
              <TextInputField
                name={`${name}.identification.drivingLicense.cardNumber`}
                control={control}
                label={LICENCE_CARD_NUMBER}
                FieldProps={{
                  description: stateCode
                    ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode].LICENCE_CARD_NUMBER_DESCRIPTION
                    : DEFAULT_LICENCE_CARD_NUMBER_DESCRIPTION,
                }}
                autoComplete="off"
              />
            </>
          ) : null}

          {isSme && identificationType === IdentificationType.DRIVING_LICENSE ? (
            <SelectField
              name={`${name}.identification.drivingLicense.state`}
              control={control}
              label={ISSUE_STATE}
              options={AU_STATES_CODE}
              placeholder="State or Territory"
            />
          ) : null}
          <Columns gap="xxlarge" collapseBelow="tablet" template={expiryDateField ? [1, 1] : [1]}>
            {expiryDateField ? (
              identificationType === IdentificationType.MEDICARE ? (
                <CustomTextInputField
                  name={expiryDateField}
                  control={control}
                  label={EXPIRY_DATE}
                  placeholder="MM/YYYY"
                  FieldProps={{ description: 'Enter expiry in MM/YYYY format' }}
                  autoComplete="off"
                  mask="mm/yyyy"
                />
              ) : (
                <DatePickerField name={expiryDateField} control={control} label={EXPIRY_DATE} autoComplete="off" />
              )
            ) : null}
            <DatePickerField
              name={`${name}.dateOfBirth`}
              control={control}
              label={DATE_OF_BIRTH}
              autoComplete="off"
              FieldProps={{
                disabled: isExistingUserDob,
                // \u00A0 is non-breaking space to align fields for medicare card.
                ...(identificationType === IdentificationType.MEDICARE && { description: '\u00A0' }),
              }}
            />
          </Columns>
        </>
      ) : null}
    </>
  );
}

export default IdentificationForm;
